import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import type { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef } from 'react';
import styles from '../GanttChart.module.scss';
import type { GanttCellParams } from '../gantt-types';
import type { GanttZoomLevel } from '../gantt-zoom';

interface Props {
  zoom: GanttZoomLevel;
  dayColumns: Array<DateTime>;
  scrollLeft: number;
  width: number;
}

interface Column {
  startDate: DateTime;
  endDate: DateTime;
  width: number;
  offset: number;
}

const GanttChartWeekGrid: React.FC<Props> = ({ zoom, dayColumns, scrollLeft }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const weekColumns = useMemo(() => {
    const columns: Array<Column> = [];
    let totalWidth = 0;
    for (let index = 0; index < dayColumns.length; index++) {
      const startDate = dayColumns[index];
      const span = 7 - startDate.weekday + 1;
      const spanOffset = span - 1;
      const endDate = dayColumns[clamp(index + spanOffset, 0, dayColumns.length - 1)];
      const width = span * zoom.columnWidth;
      columns.push({
        startDate,
        endDate,
        width,
        offset: totalWidth,
      });
      totalWidth += width;
      index += spanOffset;
    }
    return columns;
  }, [dayColumns, zoom]);

  const getWeekColumnWidth = useCallback(
    (params: { index: number }) => weekColumns[params.index].width,
    [weekColumns]
  );
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: weekColumns.length,
    getScrollElement: () => gridRef?.current,
    estimateSize: index => getWeekColumnWidth({ index }),
  });
  // Keep track of month label widths so that we can make them sticky during horizontal scroll.
  const weekLabelWidths = useRef<Record<number, number>>({});
  const setWeekLabelWidth = useCallback((columnIndex: number, element: HTMLSpanElement | null) => {
    weekLabelWidths.current[columnIndex] = element?.offsetWidth ?? 0;
  }, []);

  React.useLayoutEffect(() => {
    columnVirtualizer.scrollToOffset(scrollLeft);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  }, [scrollLeft]);

  const renderCell = useCallback(
    ({ columnIndex, key }: GanttCellParams) => {
      const column = weekColumns[columnIndex];

      const { startDate, endDate } = column;
      let secondDateFormatString;
      if (endDate.month !== startDate.month) {
        secondDateFormatString = 'MMM dd';
      } else {
        secondDateFormatString = 'dd';
      }
      const label = `${startDate.toFormat('MMM dd')} - ${endDate.toFormat(secondDateFormatString)}`;

      const labelWidth = weekLabelWidths.current[columnIndex] ?? 0;
      const PADDING = 16; // TODO import from CSS
      const left = clamp(scrollLeft - column.offset, 0, column.width - labelWidth - PADDING * 2);
      return (
        <div
          className={classNames(styles.topHeaderWeekCell, {
            [styles.firstCell]: columnIndex === 0,
          })}
          key={key}
        >
          <div className={styles.label} style={{ left }}>
            <span ref={el => setWeekLabelWidth(columnIndex, el)}>{label}</span>
          </div>
        </div>
      );
    },
    [weekColumns, weekLabelWidths, setWeekLabelWidth, scrollLeft]
  );

  return (
    <div className={styles.topHeaderRow} ref={gridRef}>
      <div
        style={{
          width: columnVirtualizer.getTotalSize(),
          height: '100%',
          position: 'relative',
        }}
      >
        {columnVirtualizer.getVirtualItems().map(virtualColumn => {
          return (
            <div
              key={virtualColumn.key}
              style={{
                display: 'flex',
                position: 'absolute',
                top: 0,
                left: 0,
                transform: `translateX(${virtualColumn.start}px)`,
              }}
            >
              <div
                style={{
                  width: getWeekColumnWidth({ index: virtualColumn.index }),
                  height: 24,
                  overflow: 'hidden',
                }}
              >
                {renderCell({
                  columnIndex: virtualColumn.index,
                  key: virtualColumn.key,
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default GanttChartWeekGrid;
